import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export type DiagnosticStep = 'method' | 'download' | 'upload' | 'report';
export type DiagnosticMode = 'manual' | 'automatic' | null;

export interface DiagnosticState {
  selectedMode: DiagnosticMode;
  selectedProblem: string | null;
  commandsCompleted: string[];
  uploadedData: string | null;
  uploadType: 'paste' | 'pcc' | null;
  inputValid: boolean;
  analysisResult: any | null;
  analysisStatus: 'idle' | 'running' | 'success' | 'error';
  creditDeducted: boolean;
  currentStep: DiagnosticStep;
  scannerDownloaded: boolean;
  uploadedFile: File | null;
  scanId: string | null;
  reportStatus: 'idle' | 'processing' | 'complete' | 'error';
  reportTier: 'free' | 'pro' | null;
  errorMessage: string | null;
  decodedPccData: any | null;
}

const INITIAL_STATE: DiagnosticState = {
  selectedMode: null,
  selectedProblem: null,
  commandsCompleted: [],
  uploadedData: null,
  uploadType: null,
  inputValid: false,
  analysisResult: null,
  analysisStatus: 'idle',
  creditDeducted: false,
  currentStep: 'method',
  scannerDownloaded: false,
  uploadedFile: null,
  scanId: null,
  reportStatus: 'idle',
  reportTier: null,
  errorMessage: null,
  decodedPccData: null,
};

interface DiagnosticContextType extends DiagnosticState {
  setSelectedMode: (mode: DiagnosticMode) => void;
  setSelectedProblem: (problem: string | null) => void;
  setUploadedData: (data: string | null, type: 'paste' | 'pcc' | null) => void;
  setInputValid: (valid: boolean) => void;
  setAnalysisResult: (result: any) => void;
  setAnalysisStatus: (status: 'idle' | 'running' | 'success' | 'error') => void;
  setCreditDeducted: (deducted: boolean) => void;
  setCurrentStep: (step: DiagnosticStep) => void;
  setScannerDownloaded: (downloaded: boolean) => void;
  setUploadedFile: (file: File | null) => void;
  setScanId: (id: string | null) => void;
  setReportStatus: (status: 'idle' | 'processing' | 'complete' | 'error') => void;
  setReportTier: (tier: 'free' | 'pro' | null) => void;
  setErrorMessage: (msg: string | null) => void;
  setDecodedPccData: (data: any | null) => void;
  resetDiagnostic: () => void;
}

const DiagnosticContext = createContext<DiagnosticContextType | undefined>(undefined);

export function DiagnosticProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<DiagnosticState>(INITIAL_STATE);
  const navigate = useNavigate();
  const location = useLocation();

  // Reset state if navigating to /diagnostic from elsewhere
  useEffect(() => {
    if (location.pathname === '/diagnostic' && state.selectedMode !== null) {
      // If we are at the root diagnostic page, we don't necessarily reset, 
      // but if the user explicitly clicks "New Diagnostic" from elsewhere, 
      // they should be calling resetDiagnostic().
    }
  }, [location.pathname]);

  const setSelectedMode = (selectedMode: DiagnosticMode) => setState(s => ({ ...s, selectedMode }));
  const setSelectedProblem = (selectedProblem: string | null) => setState(s => ({ ...s, selectedProblem }));
  const setUploadedData = (uploadedData: string | null, uploadType: 'paste' | 'pcc' | null) => 
    setState(s => ({ ...s, uploadedData, uploadType }));
  const setInputValid = (inputValid: boolean) => setState(s => ({ ...s, inputValid }));
  const setAnalysisResult = (analysisResult: any) => setState(s => ({ ...s, analysisResult }));
  const setAnalysisStatus = (analysisStatus: 'idle' | 'running' | 'success' | 'error') => 
    setState(s => ({ ...s, analysisStatus }));
  const setCreditDeducted = (creditDeducted: boolean) => setState(s => ({ ...s, creditDeducted }));
  const setCurrentStep = (currentStep: DiagnosticStep) => setState(s => ({ ...s, currentStep }));
  const setScannerDownloaded = (scannerDownloaded: boolean) => setState(s => ({ ...s, scannerDownloaded }));
  const setUploadedFile = (uploadedFile: File | null) => setState(s => ({ ...s, uploadedFile }));
  const setScanId = (scanId: string | null) => setState(s => ({ ...s, scanId }));
  const setReportStatus = (reportStatus: 'idle' | 'processing' | 'complete' | 'error') => setState(s => ({ ...s, reportStatus }));
  const setReportTier = (reportTier: 'free' | 'pro' | null) => setState(s => ({ ...s, reportTier }));
  const setErrorMessage = (errorMessage: string | null) => setState(s => ({ ...s, errorMessage }));
  const setDecodedPccData = (decodedPccData: any | null) => setState(s => ({ ...s, decodedPccData }));
  
  const resetDiagnostic = () => {
    setState(INITIAL_STATE);
    navigate('/diagnostic');
  };

  return (
    <DiagnosticContext.Provider value={{
      ...state,
      setSelectedMode,
      setSelectedProblem,
      setUploadedData,
      setInputValid,
      setAnalysisResult,
      setAnalysisStatus,
      setCreditDeducted,
      setCurrentStep,
      setScannerDownloaded,
      setUploadedFile,
      setScanId,
      setReportStatus,
      setReportTier,
      setErrorMessage,
      setDecodedPccData,
      resetDiagnostic
    }}>
      {children}
    </DiagnosticContext.Provider>
  );
}

export function useDiagnosticContext() {
  const context = useContext(DiagnosticContext);
  if (context === undefined) {
    throw new Error('useDiagnosticContext must be used within a DiagnosticProvider');
  }
  return context;
}
